import { Link } from '@tanstack/react-router';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import type {
  GetAdminV1StoresStoreIdReportsProductsEngagementSort as EngagementSort,
  StoreProductEngagementReport,
  StoreProductEngagementReportItemsItem as EngagementItem,
} from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Card, CardDescription, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

/** The metrics the endpoint sorts on. */
type Metric = 'views' | 'addToCart' | 'wishlistAdds' | 'orders' | 'units' | 'revenue';

const parseSort = (sort: EngagementSort): { metric: Metric; desc: boolean } => ({
  metric: sort.replace(/^-/, '') as Metric,
  desc: sort.startsWith('-'),
});

export interface EngagementTableProps {
  storeId: string;
  sort: EngagementSort;
  onSortChange: (sort: EngagementSort) => void;
  data: StoreProductEngagementReport | undefined;
  pending: boolean;
  error: unknown;
  onRetry: () => void;
}

function ProductCell({ storeId, item }: { storeId: string; item: EngagementItem }) {
  const { t } = useTranslation('reports');
  const link = item.storeProductId ? (
    <Link
      to="/s/$storeId/catalog/$storeProductId"
      params={{ storeId, storeProductId: item.storeProductId }}
      className="font-medium hover:underline"
    >
      {item.title}
    </Link>
  ) : (
    <Link
      to="/org/products/$productId"
      params={{ productId: item.productId }}
      className="font-medium hover:underline"
    >
      {item.title}
    </Link>
  );
  return (
    <div className="flex min-w-0 items-center gap-3">
      {item.featuredImage ? (
        <img
          src={item.featuredImage.url}
          alt={item.featuredImage.alt ?? ''}
          className="size-8 shrink-0 rounded object-cover"
          loading="lazy"
        />
      ) : (
        <span className="bg-muted size-8 shrink-0 rounded" aria-hidden />
      )}
      <div className="min-w-0">
        {link}
        <span className="text-muted-foreground block truncate text-xs">
          {item.handle ? <code>{item.handle}</code> : t('engagement.notInStore')}
        </span>
      </div>
    </div>
  );
}

/**
 * Per-product engagement in the range from the daily product rollup (D-181, decision 23): views,
 * add-to-cart, wishlist adds and sales. The endpoint returns the top 100 by the sort column;
 * a header click sorts by that metric, highest first, and a second click flips it.
 */
export function EngagementTable({
  storeId,
  sort,
  onSortChange,
  data,
  pending,
  error,
  onRetry,
}: EngagementTableProps) {
  const { t } = useTranslation('reports');
  const format = useFormat();
  const current = parseSort(sort);
  const items = data?.items;

  const header = (metric: Metric, className: string) => {
    const active = current.metric === metric;
    const next: EngagementSort = active && current.desc ? metric : `-${metric}`;
    return (
      <TableHead
        key={metric}
        className={className}
        aria-sort={active ? (current.desc ? 'descending' : 'ascending') : 'none'}
      >
        <Button
          type="button"
          variant="ghost"
          size="xs"
          className="-mr-2 tabular-nums"
          onClick={() => onSortChange(next)}
        >
          {t(`engagement.columns.${metric}`)}
          {active ? (
            current.desc ? (
              <ArrowDown className="size-3" aria-hidden />
            ) : (
              <ArrowUp className="size-3" aria-hidden />
            )
          ) : null}
        </Button>
      </TableHead>
    );
  };

  const value = (item: EngagementItem, metric: Metric) =>
    metric === 'revenue' ? format.money(item.revenue) : format.number(item[metric]);

  return (
    <Card className="gap-0 overflow-hidden py-0" data-tour="reports.products.engagement">
      <CardHeader className="border-b px-5 py-4">
        <CardTitle className="text-[15px]">{t('engagement.title')}</CardTitle>
        <CardDescription className="flex flex-wrap gap-x-3 text-xs">
          <span>{t('engagement.description')}</span>
          {data ? (
            data.rollupAt ? (
              <span title={format.utc(data.rollupAt)}>
                {t('rollup.asOf', { time: format.dateTime(data.rollupAt) })}
              </span>
            ) : (
              <span>{t('rollup.never')}</span>
            )
          ) : null}
          {data?.truncated ? <span>{t('engagement.truncated', { count: data.limit })}</span> : null}
        </CardDescription>
      </CardHeader>
      {error ? (
        <div className="p-5">
          <ErrorState error={error} onRetry={onRetry} compact />
        </div>
      ) : (
        <Table aria-label={t('engagement.title')}>
          <TableHeader>
            <TableRow>
              <TableHead className="pl-5">{t('columns.product')}</TableHead>
              {header('views', 'text-right')}
              {header('addToCart', 'hidden text-right md:table-cell')}
              {header('wishlistAdds', 'hidden text-right lg:table-cell')}
              {header('orders', 'text-right')}
              {header('units', 'hidden text-right md:table-cell')}
              {header('revenue', 'pr-5 text-right')}
            </TableRow>
          </TableHeader>
          <TableBody>
            {pending ? (
              Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={7} className="px-5">
                    <Skeleton className="h-5" />
                  </TableCell>
                </TableRow>
              ))
            ) : items && items.length > 0 ? (
              items.map((item) => (
                <TableRow key={item.productId}>
                  <TableCell className="pl-5">
                    <ProductCell storeId={storeId} item={item} />
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{value(item, 'views')}</TableCell>
                  <TableCell className="hidden text-right tabular-nums md:table-cell">
                    {value(item, 'addToCart')}
                  </TableCell>
                  <TableCell className="hidden text-right tabular-nums lg:table-cell">
                    {value(item, 'wishlistAdds')}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{value(item, 'orders')}</TableCell>
                  <TableCell className="hidden text-right tabular-nums md:table-cell">
                    {value(item, 'units')}
                  </TableCell>
                  <TableCell className="pr-5 text-right tabular-nums">
                    {value(item, 'revenue')}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} className="text-muted-foreground h-24 text-center">
                  {t('engagement.empty')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}
